import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { UserPayload } from '../../types/payloads/user.payload';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { Api } from '../api/api';
import { User } from '../../models/interfaces/user.interface';
import { users } from '../../mocks/interfaces/user.mock';
import { Sorts } from '../pagination/models/typies/sorts.type';
import { StatusPillEnum } from '../../components/status-pill/status-pill.component';
import { RolesEnum } from '../../models/enums/roles.enum';
import { sectors } from '../../mocks/interfaces/sector.mock';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';

const USER_NOT_FOUND_MESSAGE = 'Usuário não encontrado.';

export type UserGetManyParams = {
  page?: number;
  limit?: number;
  search?: string;
  sort?: Sorts;
};

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly api: Api = new Api();

  public async getManyMocked(params?: UserGetManyParams): Promise<PaginationDto<User>> {
    const page = Math.max(1, params?.page ?? 1);
    const limit = Math.max(1, params?.limit ?? 10);
    const searchValue = (params?.search || '').toLowerCase().trim();

    const filteredUsers = users.filter((user) => {
      if (!searchValue) {
        return true;
      }

      return user.name.toLowerCase().includes(searchValue)
        || user.email.toLowerCase().includes(searchValue);
    });

    const sortedUsers = [...filteredUsers].sort((left, right) => {
      if (!params?.sort?.field) {
        return 0;
      }

      const field = params.sort.field as keyof User;
      const direction = params.sort.order === 'ASC' ? 1 : -1;

      const leftValue = left[field] as string | number | Date | undefined;
      const rightValue = right[field] as string | number | Date | undefined;

      if (leftValue == null && rightValue == null) {
        return 0;
      }

      if (leftValue == null) {
        return -1 * direction;
      }

      if (rightValue == null) {
        return 1 * direction;
      }

      if (leftValue instanceof Date && rightValue instanceof Date) {
        return (leftValue.getTime() - rightValue.getTime()) * direction;
      }

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }

      return String(leftValue).localeCompare(String(rightValue)) * direction;
    });

    const total = sortedUsers.length;
    const pageCount = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * limit;
    const data = sortedUsers.slice(start, start + limit);

    return {
      data,
      page: safePage,
      pageCount,
      count: data.length,
      total,
    };
  }

  public async getOneMocked(id: number | string): Promise<User | null> {
    return users.find((user) => user.id === String(id)) ?? null;
  }

  public async createMocked(payload: UserPayload): Promise<User> {
    const name = payload.name.trim();
    const email = payload.email.trim().toLowerCase();

    if (!name || !email) {
      throw new Error('Nome e e-mail são obrigatórios.');
    }

    if (users.some((user) => user.email.toLowerCase() === email)) {
      throw new Error('Já existe um usuário com este e-mail.');
    }

    const sectorName = this.resolveSectorName(payload.sectorId);
    const now = new Date();

    const created: User = {
      id: crypto.randomUUID(),
      tenantId: 'tenant-uuid',
      tenantName: 'Unico Tenant',
      name,
      email,
      role: RolesEnum.user,
      roles: ['app:*'],
      context: 'app',
      status: StatusPillEnum.PENDING,
      sector: sectorName,
      position: payload.position?.trim() || undefined,
      createdAt: now,
      updatedAt: now,
      isActive: true,
    };

    users.unshift(created);
    return created;
  }

  public async updateMocked(id: number | string, payload: UserPayload): Promise<User | null> {
    const targetIndex = users.findIndex((user) => user.id === String(id));

    if (targetIndex < 0) {
      return null;
    }

    const current = users[targetIndex];
    const sectorName = this.resolveSectorName(payload.sectorId) ?? current.sector;

    const updated: User = {
      ...current,
      name: payload.name.trim() || current.name,
      email: payload.email.trim().toLowerCase() || current.email,
      sector: sectorName,
      position: payload.position?.trim() || undefined,
      updatedAt: new Date(),
    };

    users[targetIndex] = updated;
    return updated;
  }

  public async inviteMocked(email: string): Promise<User> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      throw new Error('E-mail é obrigatório.');
    }

    if (users.some((user) => user.email.toLowerCase() === normalizedEmail)) {
      throw new Error('Já existe um usuário com este e-mail.');
    }

    const now = new Date();
    const invited: User = {
      id: crypto.randomUUID(),
      tenantId: 'tenant-uuid',
      tenantName: 'Unico Tenant',
      email: normalizedEmail,
      name: normalizedEmail.split('@')[0],
      role: RolesEnum.user,
      roles: ['app:*'],
      context: 'app',
      status: StatusPillEnum.PENDING,
      createdAt: now,
      updatedAt: now,
      isActive: true,
    };

    users.unshift(invited);
    return invited;
  }

  public async importUsersMocked(file: File): Promise<{ imported: number }> {
    if (!file?.name) {
      throw new Error('Arquivo inválido.');
    }

    return { imported: 1 };
  }

  public async deleteMocked(id: number | string): Promise<void> {
    const targetIndex = users.findIndex((user) => user.id === String(id));

    if (targetIndex < 0) {
      throw new Error(USER_NOT_FOUND_MESSAGE);
    }

    users.splice(targetIndex, 1);
  }

  public async deactivateMocked(id: number | string): Promise<User> {
    const targetIndex = users.findIndex((user) => user.id === String(id));

    if (targetIndex < 0) {
      throw new Error(USER_NOT_FOUND_MESSAGE);
    }

    const current = users[targetIndex];
    const updated: User = {
      ...current,
      isActive: false,
      status: StatusPillEnum.INACTIVE,
      updatedAt: new Date(),
    };

    users[targetIndex] = updated;
    return updated;
  }

  // ─── Public API (fake backend ou integração real) ─────────────────────────

  public async getMany(
    params?: UserGetManyParams | CrudRequestParams<User>,
  ): Promise<PaginationDto<User>> {
    if (isFakeBackendEnabled()) {
      return this.getManyMocked(this.toUserGetManyParams(params));
    }

    const url = createCrudUrl<User>(
      environment.routes.users.base,
      this.toCrudRequestParams(params),
    );

    const response = await this.api.getAsync<PaginationDto<User>>(url);

    if (!response) {
      return {
        data: [],
        page: 0,
        pageCount: 0,
        count: 0,
        total: 0
      };
    }

    return response;
  }

  public async getOne(id: number | string): Promise<User | null> {
    if (isFakeBackendEnabled()) {
      return this.getOneMocked(id);
    }

    const response = await this.api.getAsync<User>(environment.routes.users.one(String(id)));

    if (!response)
      return null;

    return response;
  }

  public async create(payload: UserPayload): Promise<User | null> {
    if (isFakeBackendEnabled()) {
      return this.createMocked(payload);
    }

    const response = await this.api.postAsync<User>(environment.routes.users.base, this.toApiPayload(payload));

    if (!response)
      return null;

    return response;
  }

  public async update(id: number | string, payload: UserPayload): Promise<User | null> {
    if (isFakeBackendEnabled()) {
      return this.updateMocked(id, payload);
    }

    const response = await this.api.putAsync<User>(environment.routes.users.one(String(id)), this.toApiPayload(payload));

    if (!response)
      return null;

    return response;
  }

  public async invite(email: string): Promise<User> {
    if (isFakeBackendEnabled()) {
      return this.inviteMocked(email);
    }

    const response = await this.api.postAsync<User>(`${environment.routes.users.base}/invite`, { email });

    if (!response) {
      throw new Error('Falha ao convidar usuário.');
    }

    return response;
  }

  public async importUsers(file: File, updateExisting = false): Promise<{ imported: number }> {
    if (isFakeBackendEnabled()) {
      return this.importUsersMocked(file);
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('updateExisting', String(updateExisting));

    const response = await this.api.postAsync<{ imported: number }>(
      `${environment.routes.users.base}/import`,
      formData,
    );

    if (!response) {
      throw new Error('Falha ao importar usuários.');
    }

    return response;
  }

  public async delete(id: number | string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.deleteMocked(id);
    }

    await this.api.deleteAsync(environment.routes.users.one(String(id)));
  }

  public async activate(id: number | string): Promise<User> {
    if (isFakeBackendEnabled()) {
      return this.activateMocked(id);
    }

    return this.patchStatus(id, 'active', 'Falha ao ativar usuário.');
  }

  public async deactivate(id: number | string): Promise<User> {
    if (isFakeBackendEnabled()) {
      return this.deactivateMocked(id);
    }

    return this.patchStatus(id, 'inactive', 'Falha ao desativar usuário.');
  }

  public async activateMocked(id: number | string): Promise<User> {
    const targetIndex = users.findIndex((user) => user.id === String(id));

    if (targetIndex < 0) {
      throw new Error(USER_NOT_FOUND_MESSAGE);
    }

    const current = users[targetIndex];
    const updated: User = {
      ...current,
      isActive: true,
      status: StatusPillEnum.ACTIVE,
      updatedAt: new Date(),
    };

    users[targetIndex] = updated;
    return updated;
  }

  private async patchStatus(
    id: number | string,
    status: 'active' | 'inactive',
    errorMessage: string,
  ): Promise<User> {
    const response = await this.api.patchAsync<User>(`${environment.routes.users.one(String(id))}/status`, {
      status,
    });

    if (!response) {
      throw new Error(errorMessage);
    }

    return response;
  }

  private toApiPayload(payload: UserPayload): {
    name: string;
    email: string;
    imageUrl?: string;
    sector?: string;
    position?: string;
  } {
    const sector = this.resolveSectorName(payload.sectorId);
    const position = payload.position?.trim();

    return {
      name: payload.name,
      email: payload.email,
      ...(payload.imageUrl ? { imageUrl: payload.imageUrl } : {}),
      ...(sector ? { sector } : {}),
      ...(payload.position == null ? {} : { position: position || '' }),
    };
  }

  private resolveSectorName(sectorIdOrName?: string): string | undefined {
    if (!sectorIdOrName?.trim()) {
      return undefined;
    }

    const value = sectorIdOrName.trim();
    const byId = sectors.find((sector) => sector.id === value);
    if (byId) {
      return byId.name;
    }

    const byName = sectors.find((sector) => sector.name.toLowerCase() === value.toLowerCase());
    return byName?.name ?? value;
  }

  private toUserGetManyParams(
    params?: UserGetManyParams | CrudRequestParams<User>,
  ): UserGetManyParams {
    if (!params) {
      return {};
    }

    if (this.isSimpleUserGetManyParams(params)) {
      return params;
    }

    return {
      page: params.page,
      limit: params.limit,
      search: this.extractSearchValue(params.search),
      sort: params.sort?.[0] as Sorts | undefined,
    };
  }

  private isSimpleUserGetManyParams(
    params: UserGetManyParams | CrudRequestParams<User>,
  ): params is UserGetManyParams {
    if (typeof (params as UserGetManyParams).search === 'string') {
      return true;
    }

    const sort = (params as CrudRequestParams<User>).sort;
    return sort !== undefined && !Array.isArray(sort);
  }

  private toCrudRequestParams(
    params?: UserGetManyParams | CrudRequestParams<User>,
  ): CrudRequestParams<User> | undefined {
    if (!params) {
      return undefined;
    }

    if (typeof (params as UserGetManyParams).search === 'string') {
      const simple = params as UserGetManyParams;
      const searchValue = simple.search?.trim();

      return {
        page: simple.page,
        limit: simple.limit,
        sort: simple.sort ? [simple.sort] : undefined,
        search: searchValue
          ? {
              $or: [
                { name: { $contL: searchValue } },
                { email: { $contL: searchValue } },
              ],
            }
          : undefined,
      };
    }

    return params as CrudRequestParams<User>;
  }

  private extractSearchValue(search?: CrudRequestParams<User>['search']): string {
    if (!search) {
      return '';
    }

    return this.extractSearchFromCondition(search) || '';
  }

  private extractSearchFromCondition(condition: unknown): string | undefined {
    if (!condition || typeof condition !== 'object') {
      return undefined;
    }

    const record = condition as Record<string, unknown>;
    const fieldSearch = this.extractSearchFromField(record['name'])
      || this.extractSearchFromField(record['email']);

    if (fieldSearch) {
      return fieldSearch;
    }

    const andConditions = Array.isArray(record['$and']) ? record['$and'] as unknown[] : [];
    const orConditions = Array.isArray(record['$or']) ? record['$or'] as unknown[] : [];

    for (const nestedCondition of [...andConditions, ...orConditions]) {
      const nestedSearch = this.extractSearchFromCondition(nestedCondition);

      if (nestedSearch) {
        return nestedSearch;
      }
    }

    return undefined;
  }

  private extractSearchFromField(field: unknown): string | undefined {
    if (typeof field === 'string') {
      return field.toLowerCase().trim();
    }

    if (!field || typeof field !== 'object') {
      return undefined;
    }

    const operators = field as Record<string, unknown>;
    const value = operators['$contL'] ?? operators['$cont'];

    if (typeof value === 'string') {
      return value.toLowerCase().trim();
    }

    return undefined;
  }

}
